import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, readApiKey, writeApiKey } from '../api/client';
import { getBillingSubscription, signIn, signUp, usage as fetchUsage, whoAmI } from '../api/endpoints';
import type { BillingSubscriptionResponse, UsageResponse, UsageSnapshot } from '../api/types';
import type { AuthResponse } from '@verifyloop/core';

export interface SessionUser {
  id: string;
  email: string;
  role?: 'member' | 'admin';
}

export interface SessionOrganization {
  id: string;
  name: string;
}

interface AuthValue {
  /** null until the stored key has been validated against `/v1/auth/whoami`. */
  apiKey: string | null;
  ready: boolean;
  user: SessionUser | null;
  organization: SessionOrganization | null;
  scopes: readonly string[];
  keyLabel: string | null;
  plan: UsageSnapshot | null;
  credits: UsageResponse | null;
  subscription: BillingSubscriptionResponse | null;
  /** How long the last credit refresh is allowed to be stale. */
  refreshUsage: () => Promise<void>;
  /** Both return the server's `AuthResponse`, whose `apiKey` is shown only once. */
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  signUp: (email: string, password: string, organizationName?: string, agreeTerms?: boolean) => Promise<AuthResponse>;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

const USAGE_POLL_MS = 20_000;

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [apiKey, setApiKey] = useState<string | null>(() => readApiKey());
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [organization, setOrganization] = useState<SessionOrganization | null>(null);
  const [scopes, setScopes] = useState<readonly string[]>([]);
  const [keyLabel, setKeyLabel] = useState<string | null>(null);
  const [plan, setPlan] = useState<UsageSnapshot | null>(null);
  const [credits, setCredits] = useState<UsageResponse | null>(null);
  const [subscription, setSubscription] = useState<BillingSubscriptionResponse | null>(null);

  /** A rejected key is dead: drop it so every later call fails loudly, not silently. */
  const handleAuthFailure = useCallback(() => {
    writeApiKey(null);
    setApiKey(null);
    setUser(null);
    setOrganization(null);
    setScopes([]);
    setKeyLabel(null);
    setPlan(null);
    setCredits(null);
    setSubscription(null);
    setReady(true);
  }, []);

  const refreshUsage = useCallback(async () => {
    if (!readApiKey()) return;
    try {
      const snapshot = await fetchUsage();
      setCredits(snapshot);
      setPlan(snapshot);
    } catch (error) {
      if (error instanceof ApiError && error.isAuthFailure) handleAuthFailure();
    }
    try {
      const sub = await getBillingSubscription();
      setSubscription(sub);
    } catch {
      // Ignored if caller lacks billing scope or endpoint unavailable
    }
  }, [handleAuthFailure]);

  // Validate the stored key once, at mount.
  useEffect(() => {
    let cancelled = false;
    const key = readApiKey();
    if (!key) {
      setReady(true);
      return;
    }
    whoAmI()
      .then((response) => {
        if (cancelled) return;
        setUser(response.user);
        setOrganization(response.organization);
        setScopes(response.key.scopes);
        setKeyLabel(response.key.label);
        setPlan(response.plan);
        setApiKey(key);
        if (response.key.scopes.includes('billing')) {
          getBillingSubscription()
            .then((sub) => {
              if (!cancelled) setSubscription(sub);
            })
            .catch(() => undefined);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError && error.isAuthFailure) handleAuthFailure();
        else setReady(true);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [handleAuthFailure]);

  // Credits move while the user watches a run, so keep them close to current.
  useEffect(() => {
    if (!apiKey) return;
    void refreshUsage();
    const timer = window.setInterval(() => void refreshUsage(), USAGE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [apiKey, refreshUsage]);

  const signOut = useCallback(() => {
    writeApiKey(null);
    setApiKey(null);
    setUser(null);
    setOrganization(null);
    setScopes([]);
    setKeyLabel(null);
    setPlan(null);
    setCredits(null);
    setSubscription(null);
    setReady(true);
  }, []);

  /**
   * Adopt a freshly minted key. The state changes that make the session real
   * happen synchronously, and `whoami` fills in the profile afterwards, so the
   * caller can hand the plaintext key straight back to the login screen in the
   * same render batch, rather than racing a redirect past the one-time reveal.
   */
  const adopt = useCallback((key: string, scopes: readonly string[]) => {
    writeApiKey(key);
    setApiKey(key);
    setScopes(scopes);
    void whoAmI()
      .then((identity) => {
        setUser(identity.user);
        setOrganization(identity.organization);
        setScopes(identity.key.scopes);
        setKeyLabel(identity.key.label);
        setPlan(identity.plan);
        if (identity.key.scopes.includes('billing')) {
          getBillingSubscription()
            .then((sub) => setSubscription(sub))
            .catch(() => undefined);
        }
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.isAuthFailure) handleAuthFailure();
      })
      .finally(() => setReady(true));
  }, [handleAuthFailure]);

  const doSignIn = useCallback(
    async (email: string, password: string): Promise<AuthResponse> => {
      const response = await signIn({ email, password });
      adopt(response.apiKey, response.scopes);
      return response;
    },
    [adopt],
  );

  const doSignUp = useCallback(
    async (email: string, password: string, organizationName?: string, agreeTerms?: boolean): Promise<AuthResponse> => {
      const response = await signUp({
        email,
        password,
        ...(organizationName ? { organizationName } : {}),
        ...(agreeTerms !== undefined ? { agreeTerms } : {}),
      });
      adopt(response.apiKey, response.scopes);
      return response;
    },
    [adopt],
  );

  const value = useMemo<AuthValue>(
    () => ({
      apiKey,
      ready,
      user,
      organization,
      scopes,
      keyLabel,
      plan,
      credits,
      subscription,
      refreshUsage,
      signIn: doSignIn,
      signUp: doSignUp,
      signOut,
    }),
    [
      apiKey,
      ready,
      user,
      organization,
      scopes,
      keyLabel,
      plan,
      credits,
      subscription,
      refreshUsage,
      doSignIn,
      doSignUp,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

/** Convenience for pages that only render once a session exists. */
export function useOptionalAuth(): AuthValue | null {
  return useContext(AuthContext);
}